import PrimeVue from 'primevue/config'
import Aura from '@primeuix/themes/aura'
import type { App } from 'vue'

// Import PrimeVue CSS
import 'primeicons/primeicons.css'

// Import commonly used PrimeVue components
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import Card from 'primevue/card'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Dialog from 'primevue/dialog'
import Toast from 'primevue/toast'
import ToastService from 'primevue/toastservice'
import ConfirmDialog from 'primevue/confirmdialog'
import ConfirmationService from 'primevue/confirmationservice'
import Checkbox from 'primevue/checkbox'
import RadioButton from 'primevue/radiobutton'
import Textarea from 'primevue/textarea'
import Menu from 'primevue/menu'
import Menubar from 'primevue/menubar'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import Tabs from 'primevue/tabs'
import Tab from 'primevue/tab'
import TabList from 'primevue/tablist'
import TabPanels from 'primevue/tabpanels'
import Panel from 'primevue/panel'
import Accordion from 'primevue/accordion'
import AccordionPanel from 'primevue/accordionpanel'
import AccordionHeader from 'primevue/accordionheader'
import AccordionContent from 'primevue/accordioncontent'
import Avatar from 'primevue/avatar'
import Badge from 'primevue/badge'
import Chip from 'primevue/chip'
import Tag from 'primevue/tag'
import ProgressBar from 'primevue/progressbar'
import ProgressSpinner from 'primevue/progressspinner'
import Skeleton from 'primevue/skeleton'
import Divider from 'primevue/divider'
import Message from 'primevue/message'
import InlineMessage from 'primevue/inlinemessage'
import Tooltip from 'primevue/tooltip'
import Ripple from 'primevue/ripple'
import Paginator from 'primevue/paginator'
import Listbox from 'primevue/listbox'
import AutoComplete from 'primevue/autocomplete'
import MultiSelect from 'primevue/multiselect'
import DatePicker from 'primevue/datepicker'
import InputNumber from 'primevue/inputnumber'
import ToggleSwitch from 'primevue/toggleswitch'
import Drawer from 'primevue/drawer'
import Image from 'primevue/image'
import FileUpload from 'primevue/fileupload'
import Popover from 'primevue/popover'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import FloatLabel from 'primevue/floatlabel'
import ScrollPanel from 'primevue/scrollpanel'
import Toolbar from 'primevue/toolbar'
import SplitButton from 'primevue/splitbutton'
import Breadcrumb from 'primevue/breadcrumb'
import Carousel from 'primevue/carousel'
import SelectButton from 'primevue/selectbutton'

// Theme configuration
const themeConfig = {
  theme: {
    preset: Aura,
    options: {
      prefix: 'p',
      darkModeSelector: '[data-theme-mode="dark"]',
      cssLayer: {
        name: 'primevue',
        order: 'tailwind-base, primevue, tailwind-utilities'
      }
    }
  },
  ripple: true,
  inputVariant: 'outlined',
  locale: {
    accept: 'Accept',
    reject: 'Reject',
    choose: 'Choose',
    upload: 'Upload',
    cancel: 'Cancel',
    dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    today: 'Today',
    clear: 'Clear',
    weekHeader: 'Wk',
    firstDayOfWeek: 0,
    dateFormat: 'mm/dd/yy',
    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong',
    passwordPrompt: 'Enter a password',
    emptyMessage: 'No results found',
    emptyFilterMessage: 'No results found'
  }
}

export function setupPrimeVue(app: App) {
  // Setup PrimeVue
  app.use(PrimeVue, themeConfig)
  app.use(ToastService)
  app.use(ConfirmationService)

  // Register directives
  app.directive('tooltip', Tooltip)
  app.directive('ripple', Ripple)

  // Register global components
  app.component('Button', Button)
  app.component('PButton', Button)
  app.component('InputText', InputText)
  app.component('PInputText', InputText)
  app.component('Select', Select)
  app.component('PSelect', Select)
  app.component('Card', Card)
  app.component('PCard', Card)
  app.component('DataTable', DataTable)
  app.component('PDataTable', DataTable)
  app.component('Column', Column)
  app.component('PColumn', Column)
  app.component('Dialog', Dialog)
  app.component('PDialog', Dialog)
  app.component('Toast', Toast)
  app.component('PToast', Toast)
  app.component('ConfirmDialog', ConfirmDialog)
  app.component('PConfirmDialog', ConfirmDialog)
  app.component('Checkbox', Checkbox)
  app.component('PCheckbox', Checkbox)
  app.component('RadioButton', RadioButton)
  app.component('PRadioButton', RadioButton)
  app.component('Textarea', Textarea)
  app.component('PTextarea', Textarea)
  app.component('Menu', Menu)
  app.component('PMenu', Menu)
  app.component('Menubar', Menubar)
  app.component('PMenubar', Menubar)
  app.component('TabView', TabView)
  app.component('PTabView', TabView)
  app.component('TabPanel', TabPanel)
  app.component('PTabPanel', TabPanel)
  app.component('Tabs', Tabs)
  app.component('PTabs', Tabs)
  app.component('Tab', Tab)
  app.component('PTab', Tab)
  app.component('TabList', TabList)
  app.component('PTabList', TabList)
  app.component('TabPanels', TabPanels)
  app.component('PTabPanels', TabPanels)
  app.component('Panel', Panel)
  app.component('PPanel', Panel)
  app.component('Accordion', Accordion)
  app.component('PAccordion', Accordion)
  app.component('AccordionPanel', AccordionPanel)
  app.component('PAccordionPanel', AccordionPanel)
  app.component('AccordionHeader', AccordionHeader)
  app.component('PAccordionHeader', AccordionHeader)
  app.component('AccordionContent', AccordionContent)
  app.component('PAccordionContent', AccordionContent)
  app.component('Avatar', Avatar)
  app.component('PAvatar', Avatar)
  app.component('Badge', Badge)
  app.component('PBadge', Badge)
  app.component('Chip', Chip)
  app.component('PChip', Chip)
  app.component('Tag', Tag)
  app.component('PTag', Tag)
  app.component('ProgressBar', ProgressBar)
  app.component('PProgressBar', ProgressBar)
  app.component('ProgressSpinner', ProgressSpinner)
  app.component('PProgressSpinner', ProgressSpinner)
  app.component('Skeleton', Skeleton)
  app.component('PSkeleton', Skeleton)
  app.component('Divider', Divider)
  app.component('PDivider', Divider)
  app.component('Message', Message)
  app.component('PMessage', Message)
  app.component('InlineMessage', InlineMessage)
  app.component('PInlineMessage', InlineMessage)
  app.component('Paginator', Paginator)
  app.component('PPaginator', Paginator)
  app.component('Listbox', Listbox)
  app.component('PListbox', Listbox)
  app.component('AutoComplete', AutoComplete)
  app.component('PAutoComplete', AutoComplete)
  app.component('MultiSelect', MultiSelect)
  app.component('PMultiSelect', MultiSelect)
  app.component('DatePicker', DatePicker)
  app.component('PDatePicker', DatePicker)
  app.component('InputNumber', InputNumber)
  app.component('PInputNumber', InputNumber)
  app.component('ToggleSwitch', ToggleSwitch)
  app.component('PToggleSwitch', ToggleSwitch)
  app.component('Drawer', Drawer)
  app.component('PDrawer', Drawer)
  app.component('Image', Image)
  app.component('PImage', Image)
  app.component('FileUpload', FileUpload)
  app.component('PFileUpload', FileUpload)
  app.component('Popover', Popover)
  app.component('PPopover', Popover)
  app.component('IconField', IconField)
  app.component('PIconField', IconField)
  app.component('InputIcon', InputIcon)
  app.component('PInputIcon', InputIcon)
  app.component('FloatLabel', FloatLabel)
  app.component('PFloatLabel', FloatLabel)
  app.component('ScrollPanel', ScrollPanel)
  app.component('PScrollPanel', ScrollPanel)
  app.component('Toolbar', Toolbar)
  app.component('PToolbar', Toolbar)
  app.component('SplitButton', SplitButton)
  app.component('PSplitButton', SplitButton)
  app.component('Breadcrumb', Breadcrumb)
  app.component('PBreadcrumb', Breadcrumb)
  app.component('Carousel', Carousel)
  app.component('PCarousel', Carousel)
  app.component('SelectButton', SelectButton)
  app.component('PSelectButton', SelectButton)
}

export default {
  install: setupPrimeVue
}
